import jwtDecode from 'jwt-decode';
import FuseUtils from '@fuse/utils/FuseUtils';
import {
  BOOT_SESSION,
  bearerToken,
  clearImpersonationFlag,
  getImpersonationFlag,
  hasImpersonationFlag,
  isImpersonationToken,
  resolveBootSession,
  resolveOwnAccessToken,
} from 'app/impersonation/impersonationSession';
import { AxiosManager } from '../axiosManager';
/* eslint-disable camelcase */

export const ACCESS_TOKEN_KEY = 'jwt_access_token';
const REFRESH_TOKEN_KEY = 'jwt_refresh_token';

class JwtService extends FuseUtils.EventEmitter {
  constructor() {
    super();
    this.axios = AxiosManager.getInstance(process.env.REACT_APP_ADMIN_API_URL, 'api/web');
    this.axios_v1 = AxiosManager.getInstance(process.env.REACT_APP_ADMIN_API_URL, 'v1/api/web');
    AxiosManager.setInterceptors(this.axios);
  }

  init() {
    this.handleAuthentication();
  }

  handleAuthentication = () => {
    const access_token = this.getAccessToken();
    const tabSessionId = getImpersonationFlag();

    switch (
      resolveBootSession({
        accessToken: access_token,
        tabImpersonating: Boolean(tabSessionId),
        tabSessionId,
        isTokenValid: this.isAuthTokenValid,
      })
    ) {
      case BOOT_SESSION.NONE:
        clearImpersonationFlag();
        this.emit('onNoAccessToken');
        return;
      case BOOT_SESSION.VALID:
        this.setSession(access_token);
        this.emit('onAutoLogin', true);
        return;
      case BOOT_SESSION.IMPERSONATION_EXPIRED:
        // A read-only admin session has no refresh token: it just ends.
        this.logout();
        this.emit('onImpersonationExpired');
        return;
      case BOOT_SESSION.IMPERSONATION_REPLACED:
        // This tab was a read-only admin view, but another tab has stored its
        // own session since. Never continue as that identity silently; the
        // stored tokens belong to the other tab, so leave them alone.
        this.clearTabSession();
        this.emit('onImpersonationExpired');
        return;
      default:
        this.setSession(null);
        this.emit('onAutoLogout', 'access_token expired');
    }
  };

  /**
   * True for a read-only admin session: the stored token's claim, the tab
   * flag (also once the token is gone), or the claim of the token this tab
   * sends (when sessionStorage was unavailable and another tab stored its own).
   */
  isImpersonating = () =>
    isImpersonationToken(this.getAccessToken()) ||
    hasImpersonationFlag() ||
    isImpersonationToken(this.getTabAccessToken());

  /**
   * Ends this tab's session only: the tab flag and the in-memory header.
   * localStorage is shared with other tabs and may hold another tab's session.
   */
  clearTabSession = () => {
    clearImpersonationFlag();
    delete this.axios.defaults.headers.common.Authorization;
  };

  /**
   * @deprecated Should be reworked, as such endpoint does not exists
   */
  createUser = (data) => {
    return new Promise((resolve, reject) => {
      this.axios.post('/user/register', data).then((response) => {
        if (response.data.user) {
          this.setSession(response.data.access_token);
          resolve(response.data.user);
        } else {
          reject(response.data.error);
        }
      });
    });
  };

  updateUserData = (user) => this.axios.put(`/user/${user.id}`, { user });

  signInWithEmailAndPassword = async ({ reCaptcha, ...model }) => {
    try {
      const {
        data: { data },
      } = await this.axios_v1.post('/auth/signIn', model, addRecaptchaHeader(reCaptcha));
      this.setSession(data.access_token);
      this.setRefresh(data.refresh_token);

      return data;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  signIn2faCode = async (model) => {
    try {
      const {
        data: { data },
      } = await this.axios.post('/auth/2fa/signIn', model);
      this.setSession(data.access_token);
      this.setRefresh(data.refresh_token);

      return data.user;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  signUp = async ({ reCaptcha, ...model }) => {
    try {
      const {
        data: { data },
      } = await this.axios_v1.post('auth/signUp', model, addRecaptchaHeader(reCaptcha));

      return data;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  sendOtpCode = async ({ reCaptcha, ...model }) => {
    try {
      const {
        data: { statusCode },
      } = await this.axios_v1.post('auth/otp', model, addRecaptchaHeader(reCaptcha));
      return statusCode;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  turnOff2fa = async () => {
    try {
      const {
        data: { data },
      } = await this.axios.put('auth/2fa/disable', {
        accessToken: this.getAccessToken(),
      });
      return data;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  register2faCode = async (model) => {
    try {
      const {
        data: { data },
      } = await this.axios.put('/auth/2fa/register', {
        accessToken: this.getAccessToken(),
      });
      return data;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  confirm2fAuth = async (model) => {
    try {
      const {
        data: { data },
      } = await this.axios.put('/auth/2fa/confirm', model);
      this.setSession(data.access_token);
      this.setRefresh(data.refresh_token);

      return data.user;
    } catch (err) {
      throw new Error(AxiosManager.stringifyError(err));
    }
  };

  signInWithToken = async () => {
    try {
      const {
        data: { data },
      } = await this.axios.post('/auth/access-token', {
        accessToken: this.getAccessToken(),
      });

      return data;
    } catch (err) {
      this.logout();
      throw new Error('Failed to login with token.');
    }
  };

  signInWithRefreshToken = async () => {
    if (this.isImpersonating()) {
      // Never refresh a read-only admin session: there is no refresh token of
      // its own, and any other one would switch the identity.
      this.logout();
      throw new Error('Read-only session expired');
    }

    const refreshToken = this.getRefreshToken();
    try {
      const {
        data: { data },
      } = await this.axios.post('/auth/refreshAccessToken', { refreshToken });
      this.setSession(data.access_token);
      this.setRefresh(data.refresh_token);

      return data;
    } catch (err) {
      this.logout();
      throw new Error('Failed to refresh token and login.');
    }
  };

  confirmInvite = async ({ token, newUser, reCaptcha }) => {
    try {
      const {
        data: { data },
      } = await this.axios_v1.post(`invite/confirm/${token}`, newUser, addRecaptchaHeader(reCaptcha));
      this.setSession(data.access_token);
      this.setRefresh(data.refresh_token);

      return data.user;
    } catch (err) {
      // Keep the backend statusCode so the form can point the error at the
      // field it belongs to (e.g. UserPhoneAlreadyExists -> phone).
      const confirmInviteError = new Error(AxiosManager.stringifyError(err));
      confirmInviteError.statusCode = err.response?.data?.statusCode;
      throw confirmInviteError;
    }
  };

  verifyEmail = async ({ reCaptcha, ...model }) => {
    try {
      const {
        data: { data },
      } = await this.axios_v1.post(`auth/verify-email`, model, addRecaptchaHeader(reCaptcha));
      this.setSession(data.access_token);
      this.setRefresh(data.refresh_token);

      return data.user;
    } catch (err) {
      // Carries the response's statusCode along with the message so callers
      // can branch on the specific exception code (e.g. too-many-attempts)
      // instead of matching on backend-authored text.
      const verifyEmailError = new Error(AxiosManager.stringifyError(err));
      verifyEmailError.statusCode = err.response?.data?.statusCode;
      throw verifyEmailError;
    }
  };

  isAuthTokenValid = (access_token) => {
    if (!access_token) {
      return false;
    }
    const decoded = jwtDecode(access_token);
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  };

  setSession = (access_token) => {
    if (access_token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
      this.axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      delete this.axios.defaults.headers.common.Authorization;
    }
  };

  setRefresh = (refreshToken) => {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      return;
    }
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  };

  logout = () => {
    this.setSession(null);
    this.setRefresh(null);
    clearImpersonationFlag();
  };

  /** True when this tab holds a session in memory (set by its own sign-in, never by other tabs). */
  hasTabSession = () => Boolean(this.axios.defaults.headers.common.Authorization);

  /** The token this tab sends (its in-memory header), which other tabs cannot change. */
  getTabAccessToken = () => bearerToken(this.axios.defaults.headers.common.Authorization);

  getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

  /**
   * The token this tab acts with (sockets, PDF images): its own in-memory
   * one, never another tab's session that replaced it in localStorage.
   */
  getOwnAccessToken = () => resolveOwnAccessToken(this.getTabAccessToken(), this.getAccessToken());

  getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
}

export const addRecaptchaHeader = (token, config = {}) => {
  if (!config.headers) {
    config.headers = {};
  }
  config.headers['x-turnstile-token'] = token;

  return config;
};

const instance = new JwtService();

export default instance;
